import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Table, Space } from "antd";
import { UiDataTable, UiButton, UiTag } from "../../ui";
import { getTablePagination } from "../../../utils/tablePagination";
import { PHARMACY_PAGE_SIZE } from "../common/ListScreen";
import { useFittedRowsHeight } from "../common/useFittedRowsHeight";
import { usersListApi } from "../../../services/auth.services";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { ORGANISATONID } from "../../../constants/defaultKeys";
import PharmacyUserForm from "./PharmacyUserForm";

const PHARMACIST = "Pharmacist";

export const useIsPharmacyAdmin = () => {
  const { userDetails } = useContext(UserContext);
  const currentOrgId = Number(getCookie(ORGANISATONID)) || null;

  return useMemo(() => {
    const orgs = userDetails?.organizations || [];
    const org =
      orgs.find((o) => Number(o?.org_id) === currentOrgId) ||
      userDetails?.activeOrg ||
      null;
    return (org?.roles || []).some(
      (role) => role?.role === PHARMACIST && role?.is_admin === true
    );
  }, [userDetails, currentOrgId]);
};

const PharmacyUsers = () => {
  const isPharmacyAdmin = useIsPharmacyAdmin();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    setLoading(true);
    const params = { page, limit: PHARMACY_PAGE_SIZE };
    if (search) params.search = search;
    usersListApi(params)
      .then((res) => setData(res?.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [page, search, refresh]);

  /*
   * org-users-list returns every user in the organisation, so it is narrowed to
   * those actually holding a pharmacist role - this is the pharmacy module.
   */
  const rows = useMemo(() => {
    const all = data?.results || data?.data || [];
    return (Array.isArray(all) ? all : [])
      .map((user) => ({
        ...user,
        pharmacyRoles: (user.roles || []).filter((r) => r?.role === PHARMACIST),
      }))
      .filter((user) => user.pharmacyRoles.length);
  }, [data]);

  const columns = [
    {
      title: "Name",
      key: "name",
      render: (_, record) =>
        [record.first_name, record.last_name].filter(Boolean).join(" ") || "-",
    },
    { title: "Mobile", dataIndex: "mobile", key: "mobile" },
    { title: "Email", dataIndex: "email", key: "email", render: (v) => v || "-" },
    {
      title: "Facilities",
      key: "facilities",
      render: (_, record) => (
        <Space size={4} wrap>
          {record.pharmacyRoles.map((role, index) => (
            <UiTag
              key={`${role.facility_id}-${index}`}
              // A pharmacist is either an admin or ordinary staff - no HOD tier.
              tone={role.is_admin ? "warning" : "neutral"}
              size="sm"
            >
              {role.facility || "-"}
              {role.is_admin ? " · Admin" : ""}
            </UiTag>
          ))}
        </Space>
      ),
    },
  ];

  const rowsRef = useRef(null);
  const rowsHeight = useFittedRowsHeight(rowsRef, [rows, loading, page, data?.count]);

  return (
    <UiDataTable
      onSearch={(e) => {
        setSearch(e?.target?.value || "");
        setPage(1);
      }}
      searchValue={search}
      searchDebounceMs={300}
      searchPlaceholder="Search Users"
      toolbarTrailing={
        // Adding users is a Pharmacy Admin action; the endpoint enforces this
        // too, so hiding the button avoids offering an action that would 403.
        isPharmacyAdmin ? (
          <UiButton onClick={() => setOpen(true)}>Add User</UiButton>
        ) : null
      }
    >
      {/*
        * Every column, with the rows scrolling inside the table rather than
        * taking the page with them - the search bar above and the pager below
        * stay where they are, as they do on every other pharmacy list.
        */}
      <div ref={rowsRef}>
        <Table
          loading={loading}
          dataSource={rows}
          columns={columns}
          rowKey="user_id"
          scroll={{ x: "max-content", y: rowsHeight }}
          pagination={getTablePagination({
            current: page,
            pageSize: PHARMACY_PAGE_SIZE,
            total: data?.count ?? rows.length,
            onChange: (p) => setPage(p),
          })}
        />
      </div>

      {open && (
        <PharmacyUserForm
          open={open}
          setOpen={setOpen}
          onSaved={() => setRefresh((prev) => !prev)}
        />
      )}
    </UiDataTable>
  );
};

export default PharmacyUsers;
